import { policy } from '../data/policies'
import { isPhysical } from '../core/rules/stock'
import type { Product, RoutineStep } from '../core/types/catalog'
import type { Account, AccountOrder, Appointment, PlanItem, RoutineSlot, Subscription } from '../core/types/account'

// ——— Datums ———
// Eén implementatie in core/format/dates.ts. Hier doorgegeven zodat bestaande imports blijven werken.
// date-fns wordt alleen in core/format/dates.ts aangeroepen; hier nooit direct.
import { daysBetween, daysSince, daysUntil, hoursFromNow, newestFirst, shiftDate, zonedInstant } from '../core/format/dates'
export { iso, today, dayLong, dayShort, dayMedium, daysUntil, daysSince, weeksSince, shiftDate, relative, newestFirst } from '../core/format/dates'

// ——— Bestellingen ———

/** Totaal van een bestelling: wat de backend meegaf, anders regels + verzending. */
export const orderTotal = (o: AccountOrder) => o.total ?? o.lines.reduce((s, l) => s + l.price * l.qty, 0) + o.shipping

// ——— Fotodagboek ———

/**
 * Weeknummer in het dagboek: week 1 is de eerste foto, niet het begin van het
 * account. Anders krijgt een salonklant van twee jaar "week 104" als startfoto.
 */
export const diaryWeek = (firstDate: string | undefined, date: string) =>
  firstDate ? Math.max(0, Math.floor(daysBetween(firstDate, date) / 7)) + 1 : 1

// ——— Productsoorten ———

export const isRetinoid = (p: Product) => /retin/i.test(p.name) || p.keyIngredients.some((k) => /retin/i.test(k))
export const isVitaminC = (p: Product) => /vitamin(e)? c|c-tetra/i.test(p.name)

// ——— Huidplan ———

export const slotItems = (items: PlanItem[], slot: RoutineSlot) => items.filter((i) => i.slots.includes(slot))

/** "± 3 maanden" → 13, "± 6 weken" → 6. Onbekend → 10 weken, een voorzichtige schatting. */
export function weeksFromLabel(label?: string): number {
  const m = label?.match(/(\d+(?:[.,]\d+)?)\s*(maand|maanden|week|weken)/i)
  if (!m) return 10
  const n = Number(m[1].replace(',', '.'))
  return /maand/i.test(m[2]) ? Math.round(n * 4.35) : Math.round(n)
}

const stepOrder: Record<RoutineStep, number> = { Reinigen: 0, Behandelen: 2, Hydrateren: 3, Beschermen: 5 }
const orderOf = (p: Product) =>
  p.category === 'Toner' ? 1 : p.category === 'Olie' ? 4 : p.category === 'SPF' ? 5 : p.step ? stepOrder[p.step] : 3

/** Ochtend, avond of beide — zoals Sarah het standaard adviseert. */
function slotsFor(p: Product): RoutineSlot[] {
  if (p.category === 'SPF' || p.step === 'Beschermen') return ['ochtend']
  if (isRetinoid(p) || p.category === 'Olie') return ['avond']
  if (isVitaminC(p)) return ['ochtend']
  return ['ochtend', 'avond']
}

const firstSentence = (text: string) => (text.match(/^.*?[.!?](\s|$)/)?.[0] ?? text).trim()

/**
 * Een planregel met Sarahs standaardadvies voor dit product. Gebruikt voor de
 * voorlopige routine in Mijn omgeving én als startpunt in het klantdossier.
 */
export const planItemFor = (product: Product, startedOn: string): PlanItem => ({
  productId: product.id,
  slots: slotsFor(product),
  instruction: firstSentence(product.howTo),
  frequency: isRetinoid(product) ? '2–3 avonden per week om te beginnen' : undefined,
  startedOn,
  lastsWeeks: weeksFromLabel(product.usesPerBottle),
})

/** Zet planregels in de volgorde waarin je ze gebruikt: reinigen → … → SPF. */
export const sortPlanItems = (items: PlanItem[], productById: (id: string) => Product | undefined) =>
  [...items].sort((a, b) => {
    const pa = productById(a.productId)
    const pb = productById(b.productId)
    return (pa ? orderOf(pa) : 9) - (pb ? orderOf(pb) : 9)
  })

export type EffectivePlan = { items: PlanItem[]; provisional: boolean }

/**
 * Het plan dat we tonen. Heeft Sarah het huidplan ingevuld, dan dat. Zo niet,
 * dan een voorlopige routine uit wat de klant besteld heeft — in de juiste
 * volgorde, zodat ze direct weet hoe ze begint. Maskers horen er niet in (wekelijks).
 */
export function effectivePlan(account: Account, productById: (id: string) => Product | undefined): EffectivePlan {
  if (account.plan.items.length) return { items: account.plan.items, provisional: false }
  const latest = new Map<string, string>()
  ;[...account.orders]
    .sort((a, b) => a.date.localeCompare(b.date))
    .forEach((o) => o.lines.forEach((l) => latest.set(l.productId, o.date)))

  const items = Array.from(latest.entries())
    .map(([productId, date]) => ({ product: productById(productId), date }))
    .filter(
      (x): x is { product: Product; date: string } =>
        x.product !== undefined && isPhysical(x.product) && x.product.category !== 'Masker',
    )
    .sort((a, b) => orderOf(a.product) - orderOf(b.product))
    .map(({ product, date }) => planItemFor(product, date))
  return { items, provisional: true }
}

export type RefillState = {
  /** 0–1, geschat wat er nog in zit. */
  left: number
  runsOutOn: string
  level: 'ruim' | 'bijna-op' | 'op'
}

/** Schatting op basis van ingebruikname en hoe lang een verpakking bij dit gebruik meegaat. */
export function refillState(item: PlanItem): RefillState {
  const totalDays = item.lastsWeeks * 7
  const used = daysSince(item.startedOn)
  const left = Math.max(0, Math.min(1, 1 - used / totalDays))
  const runsOutOn = shiftDate(item.startedOn, totalDays)
  const daysLeft = daysUntil(runsOutOn)
  return { left, runsOutOn, level: daysLeft <= 0 ? 'op' : daysLeft <= policy.refillWarnDays ? 'bijna-op' : 'ruim' }
}

/** Een zin, niet een percentage: "Nog ± 2 weken". */
export const refillLabel = (r: RefillState) => {
  const d = daysUntil(r.runsOutOn)
  if (d <= 0) return 'Waarschijnlijk op'
  if (d < 7) return `Nog ± ${d} ${d === 1 ? 'dag' : 'dagen'}`
  const w = Math.round(d / 7)
  return `Nog ± ${w} ${w === 1 ? 'week' : 'weken'}`
}

// ——— Abonnement ———

export const nextIsGift = (sub: Subscription) => (sub.deliveriesDone + 1) % policy.subscription.giftEvery === 0
export const deliveriesUntilGift = (sub: Subscription) =>
  policy.subscription.giftEvery - (sub.deliveriesDone % policy.subscription.giftEvery)

/** De volgende leverdata. */
export function upcomingDeliveries(sub: Subscription, count = 3): string[] {
  return Array.from({ length: count }, (_, i) => shiftDate(sub.nextDelivery, i * sub.intervalWeeks * 7))
}

/** Loopt een product uit het abonnement op vóór de volgende levering? */
export const runsOutBeforeDelivery = (item: PlanItem, sub: Subscription | null) =>
  Boolean(sub && sub.status === 'actief' && refillState(item).runsOutOn < sub.nextDelivery)

/**
 * Wat eenmalig mee kan met een levering: eerst wat Sarah bij haar producten
 * adviseert, dan maskers, olie en oogverzorging. Nooit iets wat ze al krijgt.
 */
export function bumpCandidates(products: Product[], account: Account, planIds: string[], limit = 8): Product[] {
  const exclude = new Set([...planIds, ...(account.subscription?.items.map((i) => i.productId) ?? [])])
  const byId = new Map(products.map((p) => [p.id, p]))
  const pairs = planIds.flatMap((id) => byId.get(id)?.pairs ?? [])
  const extras = products.filter((p) => p.category === 'Masker' || p.category === 'Olie' || /oog|eye/i.test(p.name)).map((p) => p.id)
  return Array.from(new Set([...pairs, ...extras]))
    .filter((id) => !exclude.has(id))
    .map((id) => byId.get(id))
    .filter((p): p is Product => p !== undefined && p.kind !== 'giftcard')
    .slice(0, limit)
}

/** Producten uit eerdere bestellingen die in een abonnement mogen. */
export const refillableFromOrders = (account: Account, productById: (id: string) => Product | undefined) =>
  Array.from(new Set(account.orders.flatMap((o) => o.lines.map((l) => l.productId))))
    .map(productById)
    .filter((p): p is Product => Boolean(p?.refill))

// ——— Afspraken ———

/** Het exacte begin, in salontijd (Amsterdam). Echte verstreken uren, dus ook rond de klokwissel goed. */
export const appointmentStart = (a: Appointment) => zonedInstant(a.date, a.time)
export const hoursUntil = (a: Appointment) => hoursFromNow(appointmentStart(a))
export const canChange = (a: Appointment) => hoursUntil(a) >= policy.cancelHours

export const upcomingAppointments = (list: Appointment[]) =>
  list.filter((a) => hoursUntil(a) > -2).sort((a, b) => appointmentStart(a).getTime() - appointmentStart(b).getTime())

export const lastVisit = (account: Account) => newestFirst(account.visits)[0]

/**
 * Kuurstatus: welke sessies zijn ingepland en welke is de eerstvolgende die nog geboekt moet worden.
 * Eén implementatie voor de site én de demo-backend (demo/agendaBackend.ts).
 *
 * Wat een sessie verbruikt (`course.done`): alleen afronden. `account.appointments` bevat alleen
 * open afspraken (zonder `outcome`), dus een geannuleerde of no-show-afspraak geeft haar nummer
 * vrij en telt niet mee. Een open afspraak waarvan de tijd al voorbij is (Sarah heeft hem nog
 * niet afgerond) houdt haar nummer wél vast; anders zou hetzelfde nummer twee keer geboekt kunnen worden.
 * Het eerstvolgende nummer is het laagste vrije nummer boven `done`, zodat een gat (sessie 2
 * geannuleerd, sessie 3 staat nog) weer met 2 wordt gevuld.
 */
export function courseStatus(account: Account) {
  const course = account.course
  if (!course) return null
  const booked = account.appointments
    .flatMap((a) => (a.treatmentSlug === course.treatmentSlug && a.session ? [a.session] : []))
  const nextToBook = Array.from({ length: course.total }, (_, i) => i + 1).find((n) => n > course.done && !booked.includes(n))
  return { course, booked, nextToBook, finished: course.done >= course.total }
}

// ——— Soort klant ———

export type AccountShape = {
  /** Heeft thuisproducten (plan of bestellingen). */
  home: boolean
  /** Komt (of kwam) in de salon. */
  salon: boolean
}

export const accountShape = (account: Account): AccountShape => ({
  home: account.plan.items.length > 0 || account.orders.length > 0,
  salon: account.visits.length > 0 || account.appointments.length > 0,
})

/** Controleert of alle verwijzingen in het account bestaan. Leeg = in orde. */
export function accountIssues(
  account: Account,
  exists: { product: (id: string) => boolean; treatment: (slug: string) => boolean; routine: (id: string) => boolean },
): string[] {
  const issues: string[] = []
  const p = (id: string, where: string) => !exists.product(id) && issues.push(`${where}: onbekend product ${id}`)
  const t = (slug: string, where: string) => !exists.treatment(slug) && issues.push(`${where}: onbekende behandeling ${slug}`)
  account.plan.items.forEach((i) => p(i.productId, 'huidplan'))
  if (account.plan.routineId && !exists.routine(account.plan.routineId)) issues.push(`huidplan: onbekend protocol ${account.plan.routineId}`)
  account.subscription?.items.forEach((i) => p(i.productId, 'abonnement'))
  account.orders.forEach((o) => o.lines.forEach((l) => p(l.productId, o.id)))
  account.appointments.forEach((a) => t(a.treatmentSlug, a.id))
  account.visits.forEach((v) => t(v.treatmentSlug, 'bezoek'))
  if (account.course) t(account.course.treatmentSlug, 'kuur')
  return issues
}
